// src/components/PerlSlider.jsx
import React, { useState } from "react";
import { Box, Slider, Typography, TextField, InputAdornment } from "@mui/material";

export default function PerlSlider({
  label = "--",
  value,
  min = 0,
  max = 20,
  step = 0.5,
  unit = "%",
  onChange,
  displayPrecision = null,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    let numValue = parseFloat(inputValue);

    // Validate and clamp to min/max
    if (isNaN(numValue)) {
      numValue = value; // Revert to original
    } else {
      numValue = Math.max(min, Math.min(max, numValue));
    }

    setInputValue(numValue);

    // Trigger onChange with the same event structure as slider
    if (onChange) {
      onChange({ target: { value: numValue } }, numValue);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      handleInputBlur();
    } else if (e.key === "Escape") {
      setInputValue(value);
      setIsEditing(false);
    }
  };

  // Sync inputValue when value prop changes
  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  const formatDisplayValue = (rawValue) => {
    if (displayPrecision === null || rawValue === null || rawValue === undefined || rawValue === "") {
      return rawValue;
    }

    const numericValue = typeof rawValue === "number" ? rawValue : parseFloat(rawValue);
    if (Number.isNaN(numericValue)) {
      return rawValue;
    }

    return numericValue.toFixed(displayPrecision);
  };

  return (
    <Box>
      {/* title + live value (editable) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
          {label}:
        </Typography>
        {isEditing ? (
          <TextField
            size="small"
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            autoFocus
            inputProps={{
              min,
              max,
              step,
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
            }}
            sx={{
              width: "100px",
              "& .MuiInputBase-root": {
                height: "32px",
              },
            }}
          />
        ) : (
          <Typography
            component="span"
            sx={{
              color: "text.primary",
              cursor: "pointer",
              px: 1,
              py: 0.5,
              borderRadius: 1,
              "&:hover": {
                bgcolor: "action.hover",
              },
            }}
            onClick={() => setIsEditing(true)}
            title="Click to edit"
          >
            {formatDisplayValue(value)}
            {unit}
          </Typography>
        )}
      </Box>

      {/* slider */}
      <Slider value={value} min={min} max={max} step={step} onChange={onChange} />

      {/* min / max labels under slider */}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="caption">
          {min}
          {unit}
        </Typography>
        <Typography variant="caption">
          {max}
          {unit}
        </Typography>
      </Box>
    </Box>
  );
}
